import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import apiClient from 'api/apiConfig';
import { ServerModule } from '../ServerWorkflowType';
import { moduleFilterAtom } from '../atoms';
import { modules as defaultModules } from './modules';

const fetchModules = async (): Promise<ServerModule[]> => {
  const response = await apiClient.get<ServerModule[]>('/csb/modules');
  return response.data;
};

export const useModules = () => {
  const filterText = useAtomValue(moduleFilterAtom);

  const {
    data: modules = defaultModules,
    isLoading,
    error,
  } = useQuery<ServerModule[]>({
    queryFn: fetchModules,
    queryKey: ['modules'],
  });

  const filteredModules = useMemo(() => {
    if (!filterText.trim()) {
      return modules;
    }

    const searchTextLowerCase = filterText.toLowerCase();
    return modules.filter((module) => {
      const nameMatches = module.name
        .toLowerCase()
        .includes(searchTextLowerCase);
      const versionMatches = module.version
        .toLowerCase()
        .includes(searchTextLowerCase);
      const descriptionMatches = module.description
        ?.toLowerCase()
        .includes(searchTextLowerCase);

      return nameMatches || versionMatches || descriptionMatches;
    });
  }, [modules, filterText]);
  const systemModules = useMemo(
    () => filteredModules.filter((module) => module.isSystem),
    [filteredModules]
  );

  const nonSystemModules = useMemo(
    () => filteredModules.filter((module) => !module.isSystem),
    [filteredModules]
  );

  return {
    error,
    isLoading,
    nonSystemModules,
    systemModules,
  };
};
